import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { setRoom } from '@store/actionSlices/appConfig'
import { SortOrder } from '@store/constants'
import { RootStateTypeExtra } from '@store/types'

import {
  selectShowcaseRoomsFromResult,
  useLazyGetShowcaseRoomsQuery,
} from '@api/showcase-room'

const useDefaultRoom = () => {
  const dispatch = useDispatch()
  const currentRoom = useSelector(
    (state: RootStateTypeExtra) => state.appConfig.room
  )
  const [getShowcaseRooms] = useLazyGetShowcaseRoomsQuery()

  const getDefaultRoom = useCallback<
    (args: {
      projectName: string
      prioritizeCurrentRoom?: boolean
    }) => Promise<string | undefined>
  >(
    async ({ projectName, prioritizeCurrentRoom }) => {
      try {
        const showcaseRoomsResponse = await getShowcaseRooms({
          projectName,
          pagination: false,
          sort_order: SortOrder.ASC,
        })
        const { showcaseRooms } = selectShowcaseRoomsFromResult(
          showcaseRoomsResponse
        )

        const returnCurrentRoom = prioritizeCurrentRoom
          ? showcaseRooms.some((room) => room.name === currentRoom)
          : false

        if (returnCurrentRoom) {
          return currentRoom
        }

        const defaultRoomName = showcaseRooms.find(
          (room) => room.isDefault
        )?.name
        const showcaseRoomName = showcaseRooms[0]?.name || undefined

        if (defaultRoomName) {
          return defaultRoomName
        }

        if (showcaseRoomName) {
          return showcaseRoomName
        }
        return undefined
      } catch (error) {
        return undefined
      }
    },
    [getShowcaseRooms, currentRoom]
  )

  const setDefaultRoom = useCallback(
    (room: string) => {
      dispatch(setRoom(room))
    },
    [dispatch]
  )

  return {
    getDefaultRoom,
    setDefaultRoom,
  }
}

export default useDefaultRoom
